// ZIP code ranges by state abbreviation
const STATE_ZIP_RANGES: Record<string, [number, number][]> = {
  AL: [[35004, 36925]], AK: [[99501, 99950]], AZ: [[85001, 86556]],
  AR: [[71601, 72959], [75502, 75502]], CA: [[90001, 96162]],
  CO: [[80001, 81658]], CT: [[6001, 6928]], DE: [[19701, 19980]],
  FL: [[32004, 34997]], GA: [[30001, 31999], [39813, 39901]],
  HI: [[96701, 96898]], ID: [[83201, 83876]], IL: [[60001, 62999]],
  IN: [[46001, 47997]], IA: [[50001, 52809]], KS: [[66002, 67954]],
  KY: [[40003, 42788]], LA: [[70001, 71497]], ME: [[3901, 4992]],
  MD: [[20601, 21930]], MA: [[1001, 2791]], MI: [[48001, 49971]],
  MN: [[55001, 56763]], MS: [[38601, 39776]], MO: [[63001, 65899]],
  MT: [[59001, 59937]], NE: [[68001, 69367]], NV: [[88901, 89883]],
  NH: [[3031, 3897]], NJ: [[7001, 8989]], NM: [[87001, 88441]],
  NY: [[10001, 14975]], NC: [[27006, 28909]], ND: [[58001, 58856]],
  OH: [[43001, 45999]], OK: [[73001, 74966]], OR: [[97001, 97920]],
  PA: [[15001, 19640]], RI: [[2801, 2940]], SC: [[29001, 29948]],
  SD: [[57001, 57799]], TN: [[37010, 38589]], TX: [[75001, 79999], [88510, 88589]],
  UT: [[84001, 84784]], VT: [[5001, 5907]], VA: [[20101, 24658]],
  WA: [[98001, 99403]], WV: [[24701, 26886]], WI: [[53001, 54990]],
  WY: [[82001, 83128]], DC: [[20001, 20599]],
};

const STATE_NAME_TO_ABBR: Record<string, string> = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR',
  california: 'CA', colorado: 'CO', connecticut: 'CT', delaware: 'DE',
  florida: 'FL', georgia: 'GA', hawaii: 'HI', idaho: 'ID',
  illinois: 'IL', indiana: 'IN', iowa: 'IA', kansas: 'KS',
  kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
  massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS',
  missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM',
  'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND',
  ohio: 'OH', oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA',
  'rhode island': 'RI', 'south carolina': 'SC', 'south dakota': 'SD',
  tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT',
  virginia: 'VA', washington: 'WA', 'west virginia': 'WV',
  wisconsin: 'WI', wyoming: 'WY', 'district of columbia': 'DC',
};

export interface ValidationErrors {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  zipCode?: string;
  propertyCondition?: string;
  timeline?: string;
}

export function validateName(name: string): string | undefined {
  const trimmed = name.trim();
  if (!trimmed) return 'Full name is required.';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return 'Please enter your first and last name.';
  if (parts.some(p => p.length < 2)) return 'Each name part must be at least 2 characters.';
  if (!/^[a-zA-Z\s'\-]+$/.test(trimmed)) return 'Name can only contain letters, spaces, hyphens, and apostrophes.';
  return undefined;
}

export function validateEmail(email: string): string | undefined {
  const trimmed = email.trim();
  if (!trimmed) return 'Email address is required.';
  // Must have exactly one @, a domain with a dot, and a TLD of at least 2 chars
  const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(trimmed)) return 'Please enter a valid email address (e.g. john@example.com).';
  // Block obviously fake domains
  const domain = trimmed.split('@')[1].toLowerCase();
  const fakeDomains = ['test.com', 'example.com', 'fake.com', 'noemail.com', 'nomail.com', 'none.com', 'null.com'];
  if (fakeDomains.includes(domain)) return 'Please enter a real email address.';
  return undefined;
}

export function validatePhone(phone: string): string | undefined {
  if (!phone.trim()) return 'Phone number is required.';
  // Strip all non-digits
  const digits = phone.replace(/\D/g, '');
  if (digits.length !== 10 && !(digits.length === 11 && digits[0] === '1')) {
    return 'Please enter a valid 10-digit US phone number.';
  }
  const tenDigits = digits.length === 11 ? digits.slice(1) : digits;
  const areaCode = tenDigits.slice(0, 3);
  const exchange = tenDigits.slice(3, 6);

  // Block 555 exchange (fake numbers)
  if (exchange === '555') return 'Please enter a real phone number — 555 numbers are not valid.';
  // Area code can't start with 0 or 1
  if (areaCode[0] === '0' || areaCode[0] === '1') return 'Please enter a valid US area code.';
  // Block all-same-digit numbers like 1111111111
  if (/^(\d)\1{9}$/.test(tenDigits)) return 'Please enter a real phone number.';
  // Block sequential numbers like 1234567890
  if (tenDigits === '1234567890' || tenDigits === '0987654321') return 'Please enter a real phone number.';

  return undefined;
}

export function validateZipVsState(zip: string, state: string): string | undefined {
  if (!zip || !state) return undefined; // Only validate if both are provided
  const zipNum = parseInt(zip.replace(/\D/g, ''), 10);
  if (isNaN(zipNum) || zip.replace(/\D/g, '').length !== 5) {
    return 'ZIP code must be 5 digits.';
  }

  // Normalize state input to abbreviation
  const stateInput = state.trim();
  let abbr = stateInput.toUpperCase();
  if (abbr.length !== 2) {
    abbr = STATE_NAME_TO_ABBR[stateInput.toLowerCase()] || '';
  }

  if (!abbr || !STATE_ZIP_RANGES[abbr]) return undefined; // Unknown state, skip check

  const ranges = STATE_ZIP_RANGES[abbr];
  const valid = ranges.some(([min, max]) => zipNum >= min && zipNum <= max);
  if (!valid) return `This ZIP code doesn\'t match the state you entered. Please double-check.`;
  return undefined;
}

export function validateForm(formData: {
  name: string;
  email: string;
  phone: string;
  address: string;
  zipCode: string;
  state: string;
}): ValidationErrors {
  const errors: ValidationErrors = {};

  const nameErr = validateName(formData.name);
  if (nameErr) errors.name = nameErr;

  const emailErr = validateEmail(formData.email);
  if (emailErr) errors.email = emailErr;

  const phoneErr = validatePhone(formData.phone);
  if (phoneErr) errors.phone = phoneErr;

  if (!formData.address.trim()) errors.address = 'Property address is required.';

  const zipErr = validateZipVsState(formData.zipCode, formData.state);
  if (zipErr) errors.zipCode = zipErr;

  return errors;
}
